import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { IntlProvider as ReactIntlProvider } from "react-intl";
import { messages, locales, defaultLocale } from "./locales";
import { extraMessages } from "../i18n/extra";
import { getVersionById } from "../services/bible";
import { versionLanguageToLocale, VERSION_SELECTION_KEY } from "../services/bibleLocaleDefaults";

const LOCALE_KEY = "inoneyear-locale";

const resolveMessages = (locale: string): Record<string, string> => {
  if (messages[locale]) return messages[locale];
  const base = locale.split("-")[0];
  if (base !== locale && messages[base]) return messages[base];
  return messages["en"] || {};
};

const getInitialLocale = (): string => {
  // UI 语言跟随已保存版本列表的主版本语言（参照 holybible）
  try {
    const saved = JSON.parse(localStorage.getItem(VERSION_SELECTION_KEY) || "[]");
    if (Array.isArray(saved) && saved.length > 0) {
      const meta = getVersionById(String(saved[0]));
      if (meta) return versionLanguageToLocale(meta.language);
    }
  } catch {}
  try {
    const override = new URLSearchParams(window.location.search).get("locale");
    if (override && locales.includes(override)) return override;
    const saved = localStorage.getItem(LOCALE_KEY);
    if (saved && locales.includes(saved)) return saved;
  } catch {}
  // 参照 holybible：默认跟随浏览器语言
  return defaultLocale || "zh-CN";
};

interface LocaleCtxValue {
  locale: string;
  setLocale: (l: string) => void;
}
export const LocaleContext = createContext<LocaleCtxValue>({ locale: "zh-CN", setLocale: () => {} });
export const useLocale = () => useContext(LocaleContext);

const IntlProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [locale, setLocale] = useState<string>(getInitialLocale);
  useEffect(() => {
    try {
      localStorage.setItem(LOCALE_KEY, locale);
    } catch {}
    document.documentElement.lang = locale;
  }, [locale]);
  const msgs = useMemo(() => ({ ...resolveMessages(locale), ...extraMessages(locale) }), [locale]);
  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <ReactIntlProvider locale={locale} messages={msgs}>
        {children}
      </ReactIntlProvider>
    </LocaleContext.Provider>
  );
};

export default IntlProvider;
